import { defineContracts, endpoint } from '../featureModule'
import { identity } from './platform'

export const contracts = defineContracts(identity, {
  /** Authenticated durable setup-state bootstrap; available before product unlock. */
  workspaceSetupState: endpoint('GET', '/api/setup/state', { entitlement: null }),
  /** Global.Admin setup command: record an explicit provider-neutral MFA setup/skip decision. */
  updateWorkspaceSetupMfa: endpoint('PUT', '/api/setup/mfa', { entitlement: null }),
  /** Global.Admin setup command: install, select, or reapply the exact AI Runner placement. */
  updateWorkspaceSetupAiRunner: endpoint('POST', '/api/setup/ai-runner', { entitlement: null }),
  /** Global.Admin setup command: atomically finalize after every live validator passes. */
  finishWorkspaceSetup: endpoint('POST', '/api/setup/finish', { entitlement: null }),
  /** Global.Admin setup command: create a short-lived appliance enrollment code. */
  createSetupApplianceEnrollmentCode: endpoint('POST', '/api/appliance/enrollment-codes', { entitlement: null }),
  /**
   * GitHub webhook configuration read — decided-free (the webhook lane is
   * un-licensed by design; gating the config read would 403 a free surface).
   * Consumed by the git_repositories settings UI.
   */
  githubWebhookConfig: endpoint('GET', '/api/github-webhook/config', { entitlement: null }),
  /** System health aggregate for the header pill — decided-free. */
  systemHealth: endpoint('GET', '/api/system-health', { entitlement: null }),
  /** Customer entitlement bootstrap for the frontend (EntitlementContext) —
   *  decided-free; `?refresh=1` forces a fresh snapshot. */
  entitlementsState: endpoint('GET', '/api/entitlements/state', { entitlement: null }),
  /** Settings → Integrations catalog read (self-service opt-in integrations +
   *  configured state). Decided-free at the contract layer; the handler gates
   *  on the Settings role. */
  selfServiceIntegrations: endpoint('GET', '/api/integrations/self-service', { entitlement: null }),
  /** Settings → Integrations toggle write ({ key, enabled }); Settings.Write in
   *  the handler. Persists a customer override merged onto the entitlement
   *  snapshot at serve time. */
  setSelfServiceIntegration: endpoint('POST', '/api/integrations/self-service', { entitlement: null }),
  /** Public branding logo image (email clients + login page fetch it un-authed) —
   *  decided-free; the URL is consumed as an <img src> (publicAssets). */
  publicBrandingLogo: endpoint('GET', '/api/public/branding/logo.png', { entitlement: null }),
  /** Public effective branding config — the runtime authority consumed before
   *  sign-in through the configured customer API origin. */
  publicBrandingConfig: endpoint('GET', '/api/public/branding.json', { entitlement: null }),
  // ---- msgraph admin-consent proxy: decided-free handshake lanes (the
  //      consent flow must precede the msgraph entitlement; matches the
  //      pre-migration null gate baseline). ----
  /** POST /api/msgraph/consent/session — start an admin-consent session. */
  startConsentSession: endpoint('POST', '/api/msgraph/consent/session', { entitlement: null }),
  /** GET /api/msgraph/consent/binding — consent bindings + bundle catalog. */
  getConsentBinding: endpoint('GET', '/api/msgraph/consent/binding', { entitlement: null }),
  /** POST /api/msgraph/consent/test — probe a consented bundle. */
  testConsent: endpoint('POST', '/api/msgraph/consent/test', { entitlement: null }),
  /** DELETE /api/msgraph/consent/binding — disconnect a bundle binding. */
  disconnectConsentBinding: endpoint('DELETE', '/api/msgraph/consent/binding', { entitlement: null }),

  /**
   * AI dashboard aggregate (?days) — a cross-feature dashboard read model
   * under the decided-free /api/dashboard/ prefix (UNGATED_PREFIXES).
   * Consumed by the alerts feature's /usage page; served by the legacy
   * chain (routes/dashboard-ai.ts) via a structural fall-through
   * registration in features/platform/api.ts.
   */
  aiDashboard: endpoint('GET', '/api/dashboard/ai', { entitlement: null }),

  /**
   * Alerts dashboard aggregate (?days) — a cross-feature dashboard read model
   * under the decided-free /api/dashboard/ prefix. Handler body is alerts-owned
   * (supplied via the machine-lane registry, ruling B); consumed by the
   * platform dashboard's Alerts section.
   */
  alertsDashboard: endpoint('GET', '/api/dashboard/alerts', { entitlement: null }),
  /**
   * Execution dashboard aggregate (?days) — a cross-feature dashboard read model
   * under the decided-free /api/dashboard/ prefix. Handler body is investigate-
   * owned (supplied via the machine-lane registry, ruling B); consumed by the
   * platform dashboard's Execution section.
   */
  executionDashboard: endpoint('GET', '/api/dashboard/execution', { entitlement: null }),

  /**
   * Appliance health-metrics broker (?range&step) — an ungated machine lane
   * under /api/appliances/:id/* (decided-free; matches the pre-migration null
   * gate baseline). Handler body is appliance_bootstrap-owned (the
   * applianceMetrics.ts broker, supplied via the machine-lane registry, ruling
   * B); platform composes the gate. Consumed by the appliance_apps
   * ApplianceHealthMetricsPanel.
   */
  applianceMetricsHealth: endpoint('GET', '/api/appliances/:applianceId/metrics/health', { entitlement: null }),

  // ---- Vanity hostnames: customer self-service custom domains. Decided-free
  //      (entitlement null) — the '/api/vanity-hostnames' lane is un-licensed
  //      by design (UNGATED, admin-API backed); matches the pre-migration null
  //      gate baseline. Consumed by the platform Vanity settings tab. ----
  /** GET /api/vanity-hostnames — list the tenant's dashboard/API vanity hostnames. */
  listVanityHostnames: endpoint('GET', '/api/vanity-hostnames', { entitlement: null }),
  /** POST /api/vanity-hostnames — create a vanity hostname (kind + hostname). */
  createVanityHostname: endpoint('POST', '/api/vanity-hostnames', { entitlement: null }),
  /** POST /api/vanity-hostnames/refresh — re-check DNS/SSL status for all. */
  refreshVanityHostnames: endpoint('POST', '/api/vanity-hostnames/refresh', { entitlement: null }),
  /** DELETE /api/vanity-hostnames/:id — remove a vanity hostname + its routing. */
  deleteVanityHostname: endpoint('DELETE', '/api/vanity-hostnames/:id', { entitlement: null }),

  // ---- Self-service personal MCP access tokens (own rows). Decided-free
  //      ('/api/me/mcp-credentials', UNGATED) — platform owns the handler
  //      (operatorMcpCredentials.ts). Consumed by the `me` PersonalMcpTokensCard
  //      and the AI Governor tenant-admin shell. ----
  /** GET /api/me/mcp-credentials — list the operator's own MCP tokens. */
  listMcpCredentials: endpoint('GET', '/api/me/mcp-credentials', { entitlement: null }),
  /** POST /api/me/mcp-credentials — mint a personal MCP token (returns the secret once). */
  createMcpCredential: endpoint('POST', '/api/me/mcp-credentials', { entitlement: null }),
  /** POST /api/me/mcp-credentials/:id/revoke — revoke one of the operator's tokens. */
  revokeMcpCredential: endpoint('POST', '/api/me/mcp-credentials/:id/revoke', { entitlement: null }),
  /** DELETE /api/me/mcp-credentials/:id — delete a revoked personal token. */
  deleteMcpCredential: endpoint('DELETE', '/api/me/mcp-credentials/:id', { entitlement: null }),

  // ---- Web Push notifications: per-browser subscribe/unsubscribe. Decided-free
  //      ('/api/notifications', UNGATED) — platform owns the handler
  //      (notifications.ts). Consumed by the NotificationToggle in the Appearance
  //      settings page. ----
  /** POST /api/notifications/subscribe — register this browser's Push subscription. */
  subscribeNotifications: endpoint('POST', '/api/notifications/subscribe', { entitlement: null }),
  /** DELETE /api/notifications/unsubscribe — drop this browser's Push subscription. */
  unsubscribeNotifications: endpoint('DELETE', '/api/notifications/unsubscribe', { entitlement: null }),

  // ---- End-impersonation: decided-free (works under an expired license, before
  //      the licensing gate). Platform owns the handler (routes/auth-impersonation).
  //      Consumed by the ImpersonationBanner. ----
  /** DELETE /api/auth/impersonation — end the current impersonation session. */
  endImpersonation: endpoint('DELETE', '/api/auth/impersonation', { entitlement: null }),

  // ---- Appliance-app install / sync / vanity machine lanes: decided-free
  //      (entitlement null). These are the un-gated /api/appliance-app-* install
  //      + /api/appliance-app-sync-jobs + /api/appliances/:id/vanity machine
  //      lanes — per-app gating happens against the install row inside the
  //      handler, NOT on the path gate (licensing.ts UNGATED_PREFIXES). The
  //      handler BODY is appliance_apps-owned (applianceAppsRoutes.ts, supplied
  //      via the machine-lane registry MACHINE_LANE.applianceApps, ruling B);
  //      platform composes the null gate (a MULTI-KEY module — 11 per-app keys —
  //      cannot attribute the null gate). Same shape as applianceMetricsHealth.
  //      Consumed by the appliance_apps frontend (useAppInstalls hook,
  //      ApplianceVanityPanel, SmtpRelayDnsModal). ----
  /** GET /api/appliances/:applianceId/vanity — per-appliance vanity base + derived hostnames. */
  applianceVanityGet: endpoint('GET', '/api/appliances/:applianceId/vanity', { entitlement: null }),
  /** PUT /api/appliances/:applianceId/vanity — opt the appliance into (or change) a vanity base. */
  applianceVanitySet: endpoint('PUT', '/api/appliances/:applianceId/vanity', { entitlement: null }),
  /** DELETE /api/appliances/:applianceId/vanity — clear the vanity base (revert to platform host). */
  applianceVanityClear: endpoint('DELETE', '/api/appliances/:applianceId/vanity', { entitlement: null }),
  /** GET /api/appliance-app-installs?agent_id — installs for one appliance. */
  applianceAppInstallsList: endpoint('GET', '/api/appliance-app-installs', { entitlement: null }),
  /** POST /api/appliance-app-installs — create an install. */
  applianceAppInstallCreate: endpoint('POST', '/api/appliance-app-installs', { entitlement: null }),
  /** GET /api/appliance-app-sync-jobs?agent_id — sync jobs for one appliance. */
  applianceAppSyncJobsList: endpoint('GET', '/api/appliance-app-sync-jobs', { entitlement: null }),
  /** GET /api/appliance-app-installs/:installId — single install detail. */
  applianceAppInstallGet: endpoint('GET', '/api/appliance-app-installs/:installId', { entitlement: null }),
  /** GET /api/appliance-app-installs/:installId/metrics — app resource history and component health. */
  applianceAppInstallMetrics: endpoint('GET', '/api/appliance-app-installs/:installId/metrics', { entitlement: null }),
  /** PUT /api/appliance-app-installs/:installId — update an install's config. */
  applianceAppInstallUpdate: endpoint('PUT', '/api/appliance-app-installs/:installId', { entitlement: null }),
  /** DELETE /api/appliance-app-installs/:installId — remove an install. */
  applianceAppInstallDelete: endpoint('DELETE', '/api/appliance-app-installs/:installId', { entitlement: null }),
  /** POST /api/appliance-app-installs/:installId/reconcile — force reconcile. */
  applianceAppInstallReconcile: endpoint('POST', '/api/appliance-app-installs/:installId/reconcile', { entitlement: null }),
  /** PUT /api/appliance-app-installs/:installId/secrets/:secretKey — set a secret. */
  applianceAppInstallSecretSet: endpoint('PUT', '/api/appliance-app-installs/:installId/secrets/:secretKey', { entitlement: null }),
  /** DELETE /api/appliance-app-installs/:installId/secrets/:secretKey — clear a secret. */
  applianceAppInstallSecretDelete: endpoint('DELETE', '/api/appliance-app-installs/:installId/secrets/:secretKey', { entitlement: null }),
  /** GET /api/appliance-app-installs/:installId/ingress-policies — list ingress policies. */
  applianceAppInstallIngressPolicies: endpoint('GET', '/api/appliance-app-installs/:installId/ingress-policies', { entitlement: null }),
  /** PUT /api/appliance-app-installs/:installId/ingress-policies/:serviceKey — upsert an ingress policy. */
  applianceAppInstallIngressPolicyUpsert: endpoint('PUT', '/api/appliance-app-installs/:installId/ingress-policies/:serviceKey', { entitlement: null }),
  /** DELETE /api/appliance-app-installs/:installId/ingress-policies/:serviceKey — remove an ingress policy. */
  applianceAppInstallIngressPolicyDelete: endpoint('DELETE', '/api/appliance-app-installs/:installId/ingress-policies/:serviceKey', { entitlement: null }),
  /** POST /api/appliance-app-installs/:installId/deploy-agent — deploy the managed agent(s). */
  applianceAppInstallDeployAgent: endpoint('POST', '/api/appliance-app-installs/:installId/deploy-agent', { entitlement: null }),
  /** GET /api/appliance-app-installs/:installId/checkmk-site-settings — read CheckMK site settings. */
  applianceAppInstallCheckmkSiteSettingsGet: endpoint('GET', '/api/appliance-app-installs/:installId/checkmk-site-settings', { entitlement: null }),
  /** POST /api/appliance-app-installs/:installId/checkmk-site-settings — apply CheckMK site settings. */
  applianceAppInstallCheckmkSiteSettingsApply: endpoint('POST', '/api/appliance-app-installs/:installId/checkmk-site-settings', { entitlement: null }),
  /** POST /api/appliance-app-installs/:installId/checkmk-script-deploy — deploy the CheckMK webhook script. */
  applianceAppInstallCheckmkScriptDeploy: endpoint('POST', '/api/appliance-app-installs/:installId/checkmk-script-deploy', { entitlement: null }),
  /** GET /api/appliance-app-installs/:installId/smtp-relay/dns — desired + current SMTP-relay DNS state. */
  applianceAppInstallSmtpRelayDns: endpoint('GET', '/api/appliance-app-installs/:installId/smtp-relay/dns', { entitlement: null }),
  /** POST /api/appliance-app-installs/:installId/smtp-relay/dkim/selector — change the DKIM selector. */
  applianceAppInstallSmtpRelayDkimSelector: endpoint('POST', '/api/appliance-app-installs/:installId/smtp-relay/dkim/selector', { entitlement: null }),
  /** POST /api/appliance-app-installs/:installId/smtp-relay/dns/publish — reconcile DNS via CF. */
  applianceAppInstallSmtpRelayDnsPublish: endpoint('POST', '/api/appliance-app-installs/:installId/smtp-relay/dns/publish', { entitlement: null }),
  /** GET /api/appliance-app-installs/:installId/smtp-relay/delivery-log — recent SMTP delivery events (query: { limit? }). */
  applianceAppInstallSmtpRelayDeliveryLog: endpoint('GET', '/api/appliance-app-installs/:installId/smtp-relay/delivery-log', { entitlement: null }),
})
